import type { FieldUserRow } from "@/graphql/generated";
import { formatProductType } from "@/lib/formatProductType";

type User = Pick<FieldUserRow, "marketplace" | "productTypeName" | "label">;

export const plural = (n: number, one: string) =>
  `${n} ${one}${n === 1 ? "" : "s"}`;
const row = (u: User, more: number) => ({
  where: `${formatProductType(u.marketplace)} › ${u.productTypeName}${more ? ` and ${more} more` : ""}`,
  as: `as “${u.label}”`,
});

const isOwn = (store: string, own: string) =>
  store.toLowerCase() === own.toLowerCase();

export function otherStores(byStore: Record<string, number>, own: string) {
  const others = Object.entries(byStore).filter(([s]) => !isOwn(s, own));
  return {
    stores: others.length,
    count: others.reduce((sum, [, n]) => sum + n, 0),
  };
}

export function usedBySummary(all: User[], ownStore: string) {
  const seen = new Set<string>();
  const users = all.filter((u) => {
    const key = `${u.marketplace}|${u.productTypeName}`;
    if (isOwn(u.marketplace, ownStore)) return false;
    return !seen.has(key) && seen.add(key);
  });
  const stores = [...new Set(users.map((u) => u.marketplace))];
  const rows =
    stores.length <= 1
      ? users.map((u) => row(u, 0))
      : stores.map((m) => {
          const mine = users.filter((u) => u.marketplace === m);
          return row(mine[0], mine.length - 1);
        });
  return {
    count: users.length,
    title: users.length
      ? `Already used by ${plural(stores.length, "marketplace")}, across ${plural(users.length, "product type")}.`
      : "No other store uses this field yet.",
    rows,
  };
}
